import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  TableContainer,
  TableHead,
  TableRow,
  TableBody,
} from '@material-ui/core';
import {
  NkTable,
  NkTableHeaderCell,
  NkTableCell,
  NkNoRecordsTableCell,
  NkSelect,
} from '@nordkap/nordkap-ui';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import _ from 'lodash';
import api from '../../../infrastructure/api/api';
import { useStyles } from './styles';
import authCookie from '../../../infrastructure/api/auth-cookie';

const PositionsTable = props => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();
  const languageCode = useMemo(
    () =>
      authCookie.getLangFromCookie() === 'en'
        ? 'en-GB'
        : authCookie.getLangFromCookie() || 'sv',
    []
  );
  const changePositionTemplate = useCallback(
    (positionId, positionTemplateId) => {
      props.updatePositions(positionId, positionTemplateId);
      api.put(
        `positionFields/Assign?positionId=${positionId}&positionTemplateId=${positionTemplateId}`,
        {},
        enqueueSnackbar,
        t
      );
    },
    [enqueueSnackbar, props, t]
  );

  const getTemplateOptionLabel = positionTemplate =>
    (positionTemplate.archived ? `[${t('positionTemplate.archived')}] ` : '') +
    positionTemplate.name;

  return (
    <TableContainer>
      <NkTable>
        <TableHead>
          <TableRow>
            <NkTableHeaderCell>
              {t('position.HumanReadableId')}
            </NkTableHeaderCell>
            <NkTableHeaderCell>{t('position.Name')}</NkTableHeaderCell>
            <NkTableHeaderCell>{t('position.Amount')}</NkTableHeaderCell>
            <NkTableHeaderCell>{t('position.Date')}</NkTableHeaderCell>
            <NkTableHeaderCell>
              {t('massUpdatesPage.accountingTemplate')}
            </NkTableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {props.tableRows.length === 0 ? (
            <TableRow>
              <NkNoRecordsTableCell
                noRecordsAvailableLabel={t('massUpdatesPage.noPositionsFound')}
                columnCount={5}
              />
            </TableRow>
          ) : (
            props.tableRows.map(row => {
              let templateItems =
                props.templateOptions[
                  `${_.lowerFirst(row.positionType)}Options`
                ] || [];

              if (templateItems && templateItems.length > 0) {
                const selectedTemplate = templateItems.find(
                  pt => pt.positionTemplateId === row.positionTemplateId
                );
                templateItems = templateItems.filter(item => !item.archived);
                if (selectedTemplate && selectedTemplate.archived) {
                  templateItems.push(selectedTemplate);
                }
              }

              return (
                <TableRow key={row.id}>
                  <NkTableCell>{row.humanReadableId}</NkTableCell>
                  <NkTableCell>{row.name}</NkTableCell>
                  <NkTableCell>
                    {row.amount.toLocaleString(languageCode)} {row.currency}
                  </NkTableCell>
                  <NkTableCell>{row.date}</NkTableCell>
                  <NkTableCell>
                    <NkSelect
                      variant="outlined"
                      name={t('positionPage.template') + row.positionType}
                      value={row.positionTemplateId || ''}
                      onChange={event =>
                        changePositionTemplate(row.id, event.target.value)
                      }
                      items={
                        templateItems &&
                        templateItems.map(pt => ({
                          value: pt.positionTemplateId,
                          title: getTemplateOptionLabel(pt),
                        }))
                      }
                      itemPropName="value"
                      itemTitlePropName="title"
                      wrapper={false}
                      inputClasses={{ root: classes.PositionTemplateSelect }}
                    />
                  </NkTableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </NkTable>
    </TableContainer>
  );
};

PositionsTable.propTypes = {
  tableRows: PropTypes.array,
  templateOptions: PropTypes.object,
  updatePositions: PropTypes.func,
};

export default PositionsTable;
